import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';

import { UitkStepInput } from 'uitk-react-step-input';
import { UitkTertiaryButton } from 'uitk-react-button';

import { ChildrenAges } from './children-ages';
import { RoomPickerModel } from 'components/common/room-picker/room-picker-model';
import { RoomProps } from 'components/common/room-picker/internals/room';

const TertiaryButtonTracked = ClickTracker(UitkTertiaryButton);

interface MultiItemRoomProps extends RoomProps {
  onInfantAgeUpdated: (roomIndex, childIndex, target) => void;
  onRoomInfantsChanged: (room, event) => void;
}

export const MultiItemRoom = ({
  analytics,
  roomIndex,
  headerRef,
  l10n,
  onChildAgeUpdated,
  onRoomAdultsChanged,
  onRoomChildrenChanged,
  onRemoveRoom,
  room,
  selection,
  onInfantAgeUpdated,
  onRoomInfantsChanged,
}: MultiItemRoomProps) => {
  const { roomCount } = selection;
  const stepInputAnalytics = analytics.setupTracker(':Guest', 'Adjust guest count in change search');

  const handleChildAgeUpdated = (childIndex, target) => onChildAgeUpdated(roomIndex, childIndex, target);
  const handleInfantAgeUpdated = (childIndex, target) => onInfantAgeUpdated(roomIndex, childIndex, target);
  const adultsChanged = event => onRoomAdultsChanged(room, event);
  const childrenChanged = event => onRoomChildrenChanged(room, event);
  const infantsChanged = event => onRoomInfantsChanged(room, event);
  const roomRemoved = () => onRemoveRoom(room);
  const roomIndexHeader = roomIndex + 1;
  const isLastRoom = selection.roomCount === roomIndexHeader;
  const totalGuests = selection.counts.guests;
  const infants = room.children.filter(age => age < 2);
  const children = room.children.filter(age => age >= 2);

  return (
    <div key={`room-${roomCount}-${roomIndex}`} className="for-real uitk-list-divider all-y-padding-three">
      <h3
        tabIndex={0}
        className="no-outline uitk-type-200 uitk-type-medium"
        ref={isLastRoom ? headerRef : null}
        aria-label={l10n.formatText('hotels.roomPicker.room.a11yLabelRoom', roomIndexHeader, roomCount)}
      >
        <span aria-hidden="true">{l10n.formatText('hotels.roomPicker.room.title', roomIndexHeader)}</span>
      </h3>
      <UitkStepInput
        title={l10n.formatText('hotels.roomPicker.adults.text')}
        increaseText={l10n.formatText('hotels.roomPicker.increaseAdults.text', roomCount)}
        increaseId={`room-${roomCount}-${roomIndex}-adults-increase`}
        decreaseText={l10n.formatText('hotels.roomPicker.decreaseAdults.text', roomCount)}
        max={RoomPickerModel.determineMaxGuests(room.adults, totalGuests)}
        decreaseId={`room-${roomCount}-${roomIndex}-adults-decrease`}
        min={1}
        value={room.adults}
        onChange={adultsChanged}
        analytics={stepInputAnalytics}
        id={`room-${roomCount}-${roomIndex}-adults`}
      />
      <UitkStepInput
        title={l10n.formatText('hotels.roomPicker.children.text')}
        increaseText={l10n.formatText('hotels.roomPicker.increaseChildren.text', roomCount)}
        increaseId={`room-${roomCount}-${roomIndex}-children-increase`}
        decreaseText={l10n.formatText('hotels.roomPicker.decreaseChildren.text', roomCount)}
        decreaseId={`room-${roomCount}-${roomIndex}-children-decrease`}
        caption={l10n.formatText('hotels.roomPicker.multiitem.children.caption')}
        max={RoomPickerModel.determineMaxGuests(children.length, totalGuests)}
        min={0}
        value={children.length}
        onChange={childrenChanged}
        analytics={stepInputAnalytics}
        id={`room-${roomCount}-${roomIndex}-children`}
      />
      {children.length > 0 && (
        <div className="uitk-grid all-y-gutter-two all-x-gutter-two">
          {children.map((childAge, childIndex) => (
            <ChildrenAges
              childAge={childAge}
              childIndex={childIndex}
              key={childIndex}
              l10n={l10n}
              onChildAgeUpdated={handleChildAgeUpdated}
              room={room}
              roomIndex={roomIndex}
              selectLabel={l10n.formatText('hotels.roomPicker.childAgeInput.label', childIndex + 1)}
              isInfant={false}
              minChildAge={2}
              maxChildAge={17}
            />
          ))}
        </div>
      )}
      <UitkStepInput
        title={l10n.formatText('hotels.roomPicker.multiitem.infants.text')}
        increaseText={l10n.formatText('hotels.roomPicker.multiitem.increaseInfants', roomCount)}
        increaseId={`room-${roomCount}-${roomIndex}-infants-increase`}
        decreaseText={l10n.formatText('hotels.roomPicker.multiitem.decreaseInfants', roomCount)}
        decreaseId={`room-${roomCount}-${roomIndex}-infants-decrease`}
        caption={l10n.formatText('hotels.roomPicker.multiitem.infants.caption')}
        max={RoomPickerModel.determineMaxGuests(infants.length, totalGuests)}
        min={0}
        value={infants.length}
        onChange={infantsChanged}
        analytics={stepInputAnalytics}
        id={`room-${roomCount}-${roomIndex}-infants`}
      />
      {infants.length > 0 && (
        <div className="uitk-grid all-y-gutter-two all-x-gutter-two">
          {infants.map((childAge, childIndex) => (
            <ChildrenAges
              childAge={childAge}
              childIndex={childIndex}
              key={childIndex}
              l10n={l10n}
              onChildAgeUpdated={handleInfantAgeUpdated}
              room={room}
              roomIndex={roomIndex}
              selectLabel={l10n.formatText('hotels.roomPicker.multiitem.infantAgeInput.label', childIndex + 1)}
              isInfant={true}
              minChildAge={0}
              maxChildAge={1}
            />
          ))}
        </div>
      )}
      {roomIndex !== 0 && (
        <div className="uitk-type-right all-t-padding-four">
          <TertiaryButtonTracked
            data-stid="remove-room"
            linkName="Remove room"
            onClick={roomRemoved}
            rfrr=":RemoveRoom"
            text={l10n.formatText('hotels.roomPicker.removeRoomButton.label')}
            aria-label={l10n.formatText('hotels.roomPicker.removeRoomButton.a11yLabel', roomIndexHeader, roomCount)}
          />
        </div>
      )}
    </div>
  );
};
